import type { IndicatorBand } from '@risksmart-app/domain/src/types/indicator-banding-config';

const OPERATOR_GLYPHS = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
} as const;

/**
 * Human-readable threshold rule for a band, from its lower/upper boundary
 * pair — e.g. "< 20", "≥ 80", "≥ 20 and < 50". `unit` (the indicator's
 * `Unit`) is appended to each bound value when present, matching the
 * `${value} ${Unit}` convention used elsewhere for indicator results
 * (see `pages/indicators/config.tsx`).
 */
export const formatBandRule = (
  band: Pick<IndicatorBand, 'lower' | 'upper'>,
  unit?: string | null
): string => {
  const { lower, upper } = band;
  const withUnit = (value: number): string =>
    unit ? `${value} ${unit}` : `${value}`;

  if (lower && upper) {
    return `${OPERATOR_GLYPHS[lower.operator]} ${withUnit(lower.value)} and ${OPERATOR_GLYPHS[upper.operator]} ${withUnit(upper.value)}`;
  }
  if (upper) {
    return `${OPERATOR_GLYPHS[upper.operator]} ${withUnit(upper.value)}`;
  }
  if (lower) {
    return `${OPERATOR_GLYPHS[lower.operator]} ${withUnit(lower.value)}`;
  }

  // Unreachable for a schema-valid band (`indicatorBandSchema` requires at
  // least one of lower/upper) — defensive fallback only.
  return '-';
};
